/**
 * 10.9HI — EFEITOS DAS MAGIAS (a mesma reforma visual das armas, agora nas magias).
 *
 * Antes toda magia era "círculo no chão + explosão de partículas". Aqui cada tipo ganha leitura
 * própria, barata (malhas aditivas simples, sem alocação por quadro além do próprio efeito):
 *   · bolt      — RAIO de verdade entre dois pontos (fita serrilhada + núcleo branco), Corrente de Raios
 *   · streak    — RASTRO da investida/salto (fita que some)
 *   · wavefront — CRESCENTE viajando pela linha (Onda de Choque / Flecha Perfurante)
 *   · rune      — CÍRCULO RÚNICO girando no chão enquanto a zona/armadilha existe (segue o alvo se pedir)
 *   · blades    — LÂMINAS orbitando o Caçador (Redemoinho)
 *   · halo      — ARO de energia no peito durante um buff
 *   · meteor    — BOLA DE FOGO caindo do céu até o ponto do impacto
 *   · scorch    — MARCA QUEIMADA no chão (some em ~4 s)
 * Tudo some sozinho; `clear()` ao trocar de mundo/arena.
 */
import * as THREE from "three";
import { decalTexture } from "../world/GroundTextures";

type Kind = "bolt" | "streak" | "front" | "rune" | "blades" | "halo" | "fall" | "scorch";
interface Item {
  obj: THREE.Object3D;
  mats: THREE.Material[];
  kind: Kind;
  t: number;
  dur: number;
  follow?: () => THREE.Vector3;
  data?: { dx?: number; dz?: number; len?: number; y0?: number; r?: number };
  stopped?: boolean;
}
export interface VfxHandle { stop(): void }

/** Fita horizontal (plano no chão/altura) ligando pontos com largura `w`; cor por vértice pra apagar nas pontas. */
function ribbon(pts: { x: number; z: number }[], y: number, w: number, fadeEnds: boolean): THREE.BufferGeometry {
  const n = pts.length;
  const pos = new Float32Array(n * 2 * 3);
  const col = new Float32Array(n * 2 * 3);
  const idx: number[] = [];
  for (let i = 0; i < n; i++) {
    const a = pts[Math.max(0, i - 1)], b = pts[Math.min(n - 1, i + 1)];
    let dx = b.x - a.x, dz = b.z - a.z;
    const l = Math.hypot(dx, dz) || 1;
    dx /= l; dz /= l;
    const nx = -dz * w * 0.5, nz = dx * w * 0.5;
    const o = i * 6;
    pos[o] = pts[i].x + nx; pos[o + 1] = y; pos[o + 2] = pts[i].z + nz;
    pos[o + 3] = pts[i].x - nx; pos[o + 4] = y; pos[o + 5] = pts[i].z - nz;
    const f = fadeEnds ? Math.sin((i / (n - 1)) * Math.PI) * 0.7 + 0.3 : 1;
    col[o] = col[o + 1] = col[o + 2] = f; col[o + 3] = col[o + 4] = col[o + 5] = f;
    if (i < n - 1) { const a0 = i * 2; idx.push(a0, a0 + 1, a0 + 2, a0 + 1, a0 + 3, a0 + 2); }
  }
  const g = new THREE.BufferGeometry();
  g.setIndex(idx);
  g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  g.setAttribute("color", new THREE.BufferAttribute(col, 3));
  return g;
}
function addMat(color: number, opacity = 1, vertexColors = true): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, vertexColors });
}

export class SpellVfx {
  private readonly group = new THREE.Group();
  private readonly items: Item[] = [];
  private runeTex: THREE.Texture | null = null;
  private scorchTex: THREE.Texture | null = null;

  constructor(scene: THREE.Scene) {
    scene.add(this.group);
  }

  private push(it: Item): VfxHandle {
    this.group.add(it.obj);
    this.items.push(it);
    return { stop: () => { it.stopped = true; } };
  }

  /** RAIO entre dois pontos (serrilhado + ramo curto + núcleo branco). */
  bolt(x0: number, z0: number, x1: number, z1: number, color: number): void {
    const n = 9;
    const pts: { x: number; z: number }[] = [];
    const dx = x1 - x0, dz = z1 - z0, l = Math.hypot(dx, dz) || 1;
    const nx = -dz / l, nz = dx / l;
    for (let i = 0; i < n; i++) {
      const f = i / (n - 1);
      const j = i === 0 || i === n - 1 ? 0 : (Math.random() - 0.5) * Math.min(1.4, l * 0.22);
      pts.push({ x: x0 + dx * f + nx * j, z: z0 + dz * f + nz * j });
    }
    const g = new THREE.Group();
    g.add(new THREE.Mesh(ribbon(pts, 1.15, 0.34, false), addMat(color, 0.9)));
    g.add(new THREE.Mesh(ribbon(pts, 1.17, 0.11, false), addMat(0xffffff, 0.95)));
    // ramo: sai do meio e some
    const mid = pts[Math.floor(n / 2)];
    const br = [mid, { x: mid.x + nx * 1.2 + (Math.random() - 0.5), z: mid.z + nz * 1.2 + (Math.random() - 0.5) }, { x: mid.x + nx * 2.1, z: mid.z + nz * 2.1 + (Math.random() - 0.5) }];
    g.add(new THREE.Mesh(ribbon(br, 1.1, 0.16, true), addMat(color, 0.7)));
    this.push({ obj: g, mats: g.children.map((c) => (c as THREE.Mesh).material as THREE.Material), kind: "bolt", t: 0, dur: 0.22 });
  }

  /** RASTRO da investida: fita do ponto de partida até a chegada, apaga em 0,35 s. */
  streak(x0: number, z0: number, x1: number, z1: number, color: number): void {
    const pts = [{ x: x0, z: z0 }, { x: (x0 + x1) / 2, z: (z0 + z1) / 2 }, { x: x1, z: z1 }];
    const g = new THREE.Group();
    g.add(new THREE.Mesh(ribbon(pts, 0.9, 1.3, true), addMat(color, 0.55)));
    g.add(new THREE.Mesh(ribbon(pts, 0.92, 0.35, true), addMat(0xffffff, 0.6)));
    this.push({ obj: g, mats: g.children.map((c) => (c as THREE.Mesh).material as THREE.Material), kind: "streak", t: 0, dur: 0.35 });
  }

  /** CRESCENTE viajando pela linha da onda (do Caçador até `len`). */
  wavefront(x: number, z: number, dx: number, dz: number, len: number, hw: number, color: number): void {
    const m = new THREE.Mesh(new THREE.TorusGeometry(Math.max(1.2, hw * 1.15), 0.14, 6, 22, Math.PI * 0.9), addMat(color, 0.9, false));
    m.position.set(x, 0.6, z);
    // torus no plano XY → deita (X) e aponta o "arco" pra frente
    m.rotation.set(-Math.PI / 2, 0, Math.atan2(dx, dz) - Math.PI / 2 - Math.PI * 0.45);
    this.push({ obj: m, mats: [m.material as THREE.Material], kind: "front", t: 0, dur: 0.3, data: { dx, dz, len } });
  }

  /** CÍRCULO RÚNICO no chão por `seconds` (gira, pulsa); `follow` = acompanha (Redemoinho). */
  rune(x: number, z: number, r: number, color: number, seconds: number, follow?: () => THREE.Vector3): VfxHandle {
    this.runeTex ??= decalTexture("rune");
    const mat = new THREE.MeshBasicMaterial({ map: this.runeTex, color, transparent: true, opacity: 0.85, depthWrite: false, blending: THREE.AdditiveBlending });
    const m = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    m.rotation.x = -Math.PI / 2;
    m.position.set(x, 0.07, z);
    m.scale.setScalar(r * 2.1);
    return this.push({ obj: m, mats: [mat], kind: "rune", t: 0, dur: seconds, follow, data: { r } });
  }

  /** LÂMINAS orbitando (Redemoinho): 3 gumes girando rápido na altura do peito. */
  blades(follow: () => THREE.Vector3, r: number, color: number, seconds: number): VfxHandle {
    const g = new THREE.Group();
    const mat = addMat(color, 0.85, false);
    for (let i = 0; i < 3; i++) {
      const b = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 0.28), mat);
      const a = (i / 3) * Math.PI * 2;
      b.position.set(Math.cos(a) * r, 0.95, Math.sin(a) * r);
      b.rotation.set(-Math.PI / 2, 0, -a);
      g.add(b);
    }
    const ring = new THREE.Mesh(new THREE.TorusGeometry(r, 0.05, 6, 32), mat);
    ring.position.y = 0.95; ring.rotation.x = Math.PI / 2;
    g.add(ring);
    return this.push({ obj: g, mats: [mat], kind: "blades", t: 0, dur: seconds, follow, data: { r } });
  }

  /** ARO de energia no peito durante o buff. */
  halo(follow: () => THREE.Vector3, color: number, seconds: number): VfxHandle {
    const mat = addMat(color, 0.8, false);
    const m = new THREE.Mesh(new THREE.TorusGeometry(0.85, 0.06, 6, 28), mat);
    m.rotation.x = Math.PI / 2 + 0.3;
    return this.push({ obj: m, mats: [mat], kind: "halo", t: 0, dur: seconds, follow });
  }

  /** METEORO caindo do céu por `delay` s até (x, z). */
  meteor(x: number, z: number, delay: number, color: number): void {
    const g = new THREE.Group();
    const core = new THREE.Mesh(new THREE.SphereGeometry(0.5, 10, 8), new THREE.MeshBasicMaterial({ color: 0xfff0d0 }));
    const glow = new THREE.Mesh(new THREE.SphereGeometry(0.95, 10, 8), addMat(color, 0.55, false));
    g.add(core, glow);
    const tail = new THREE.Mesh(new THREE.ConeGeometry(0.7, 4.5, 8, 1, true), addMat(color, 0.4, false));
    tail.position.set(0, 2.4, 0);
    g.add(tail);
    g.position.set(x, 18, z);
    this.push({ obj: g, mats: [core.material as THREE.Material, glow.material as THREE.Material, tail.material as THREE.Material], kind: "fall", t: 0, dur: Math.max(0.2, delay), data: { y0: 18 } });
  }

  /** MARCA QUEIMADA no chão, some em 4 s. */
  scorch(x: number, z: number, r: number): void {
    this.scorchTex ??= decalTexture("crack");
    const mat = new THREE.MeshBasicMaterial({ map: this.scorchTex, color: 0x120a10, transparent: true, opacity: 0.85, depthWrite: false });
    const m = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    m.rotation.set(-Math.PI / 2, 0, Math.random() * Math.PI * 2);
    m.position.set(x, 0.04, z);
    m.scale.setScalar(r * 2.2);
    this.push({ obj: m, mats: [mat], kind: "scorch", t: 0, dur: 4 });
  }

  update(dt: number): void {
    for (let i = this.items.length - 1; i >= 0; i--) {
      const it = this.items[i];
      it.t += dt;
      const k = Math.min(1, it.t / it.dur);
      if (it.stopped || k >= 1) { this.remove(i); continue; }
      const fade = it.kind === "scorch" ? (k < 0.6 ? 1 : 1 - (k - 0.6) / 0.4) : it.kind === "rune" || it.kind === "blades" || it.kind === "halo" ? (k > 0.85 ? (1 - k) / 0.15 : 1) : 1 - k;
      for (const m of it.mats) {
        const mb = m as THREE.MeshBasicMaterial;
        const base = (mb.userData.base ??= mb.opacity) as number; // opacidade de pico guardada na 1ª passada
        mb.opacity = base * fade;
      }
      if (it.follow) { const p = it.follow(); it.obj.position.x = p.x; it.obj.position.z = p.z; if (it.kind === "halo") it.obj.position.y = p.y + 1.05 + Math.sin(it.t * 4) * 0.08; }
      switch (it.kind) {
        case "front": { const d = it.data!; it.obj.position.x += d.dx! * (d.len! / it.dur) * dt; it.obj.position.z += d.dz! * (d.len! / it.dur) * dt; break; }
        case "rune": it.obj.rotation.z += dt * 0.9; it.obj.scale.setScalar(it.data!.r! * 2.1 * (1 + Math.sin(it.t * 3) * 0.04)); break;
        case "blades": it.obj.rotation.y -= dt * 9; break;
        case "halo": it.obj.rotation.z += dt * 2.5; break;
        case "fall": { const y0 = it.data!.y0!; it.obj.position.y = y0 - (y0 - 0.6) * (k * k); it.obj.rotation.y += dt * 6; break; }
        case "streak": it.obj.scale.y = 1; break;
        default: break;
      }
    }
  }

  clear(): void {
    for (let i = this.items.length - 1; i >= 0; i--) this.remove(i);
  }

  private remove(i: number): void {
    const it = this.items[i];
    this.group.remove(it.obj);
    it.obj.traverse((o) => { const g = (o as THREE.Mesh).geometry; if (g) g.dispose(); });
    for (const m of it.mats) m.dispose();
    this.items.splice(i, 1);
  }
}
